import { useState, useMemo } from 'react'
import { ExternalLink, Edit2, Trash2, ArrowUpDown, Star, Tags, Check, Edit3, Keyboard, GripVertical, AlertCircle, Eye, BarChart2, ChevronLeft, ChevronRight } from 'lucide-react'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import { useBookmarkStore } from '../store/bookmarkStore'
import { Button } from './Button'
import { BookmarkForm } from './BookmarkForm'
import { SearchBar } from './SearchBar'
import { ConfirmDialog } from './ConfirmDialog'
import { ImportExport } from './ImportExport'
import { BatchEditDialog } from './BatchEditDialog'
import { HotkeysHelp } from './HotkeysHelp'
import { useHotkeys } from '../hooks/useHotkeys'
import { Bookmark } from '../types/bookmark'
import { BookmarkValidator } from './BookmarkValidator'
import { BookmarkPreview } from './BookmarkPreview'
import { BookmarkAnalytics } from './BookmarkAnalytics'
import { ContextMenu } from './ContextMenu'

type SortField = 'title' | 'createdAt' | 'updatedAt'
type SortOrder = 'asc' | 'desc'

export function BookmarkList() {
  const {
    bookmarks,
    categories,
    selectedBookmarks,
    removeBookmark,
    removeBookmarks,
    toggleBookmarkSelection,
    clearBookmarkSelection,
    toggleBookmarkFavorite,
    addTagToBookmark,
    removeTagFromBookmark,
    reorderBookmarks,
    theme,
  } = useBookmarkStore()

  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [bookmarkToDelete, setBookmarkToDelete] = useState<Bookmark | null>(null)
  const [showTagInput, setShowTagInput] = useState<string | null>(null)
  const [newTag, setNewTag] = useState('')
  const [showBatchEdit, setShowBatchEdit] = useState(false)
  const [showHotkeys, setShowHotkeys] = useState(false)
  const [showValidator, setShowValidator] = useState(false)
  const [previewBookmark, setPreviewBookmark] = useState<Bookmark | null>(null)
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; url: string } | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(16) // 每页显示16个书签，适合4列布局

  // 添加键盘快捷键
  useHotkeys({
    'N': () => setShowForm(true),
    'F': () => setSearchQuery(''),
    'ESC': () => {
      setSearchQuery('')
      clearBookmarkSelection()
    },
    'Ctrl+A': () => {
      bookmarks.forEach(bookmark => {
        if (!selectedBookmarks.has(bookmark.id)) {
          toggleBookmarkSelection(bookmark.id)
        }
      })
    },
    'DELETE': () => {
      if (selectedBookmarks.size > 0) {
        if (confirm(`确定要删除选中的 ${selectedBookmarks.size} 个书签吗？`)) {
          removeBookmarks([...selectedBookmarks])
        }
      }
    },
    '?': () => setShowHotkeys(true),
  })

  const getCategoryName = (categoryId?: string) => {
    if (!categoryId) return '无分类'
    return categories.find(c => c.id === categoryId)?.name || '无分类'
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const handleAddTag = (bookmarkId: string) => {
    if (newTag.trim()) {
      addTagToBookmark(bookmarkId, newTag.trim())
      setNewTag('')
      setShowTagInput(null)
    }
  }

  const filteredAndSortedBookmarks = useMemo(() => {
    return bookmarks
      .filter(bookmark => {
        const searchLower = searchQuery.toLowerCase()
        return (
          bookmark.title.toLowerCase().includes(searchLower) ||
          bookmark.description?.toLowerCase().includes(searchLower) ||
          getCategoryName(bookmark.categoryId).toLowerCase().includes(searchLower) ||
          bookmark.tags.some(tag => tag.toLowerCase().includes(searchLower))
        )
      })
      .sort((a, b) => {
        const order = sortOrder === 'asc' ? 1 : -1
        if (sortField === 'title') {
          return order * a.title.localeCompare(b.title)
        }
        return order * (a[sortField] - b[sortField])
      })
  }, [bookmarks, searchQuery, sortField, sortOrder])

  // 计算总页数
  const totalPages = Math.ceil(filteredAndSortedBookmarks.length / itemsPerPage)
  
  // 获取当前页的书签
  const currentBookmarks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filteredAndSortedBookmarks.slice(startIndex, startIndex + itemsPerPage)
  }, [filteredAndSortedBookmarks, currentPage, itemsPerPage])

  // 页面变化处理函数
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // 当筛选条件变化时，重置为第一页
  useMemo(() => {
    setCurrentPage(1)
  }, [searchQuery, sortField, sortOrder])

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return
    }

    reorderBookmarks(result.source.index, result.destination.index)
  }

  return (
    <div className="space-y-4">
      {/* 顶部工具栏 */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="搜索书签... (按 F 聚焦)"
          />
        </div>
        <div className="flex space-x-2">
          <Button onClick={() => setShowForm(true)} className="flex items-center">
            <span className="hidden sm:inline">添加书签</span>
            <span className="sm:ml-1">(N)</span>
          </Button>
          <ImportExport />
          <Button
            variant="outline"
            onClick={() => setShowValidator(true)}
            className="flex items-center"
            title="检查书签有效性"
          >
            <AlertCircle className="w-5 h-5" />
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowAnalytics(true)}
            className="flex items-center"
            title="统计分析"
          >
            <BarChart2 className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowHotkeys(true)}
            className="flex items-center"
            title="查看键盘快捷键 (?)"
          >
            <Keyboard className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* 批量操作工具栏 */}
      {selectedBookmarks.size > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg flex items-center justify-between">
          <span className="text-blue-700">
            已选择 {selectedBookmarks.size} 个书签
          </span>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                bookmarks.forEach(bookmark => {
                  if (!selectedBookmarks.has(bookmark.id)) {
                    toggleBookmarkSelection(bookmark.id)
                  }
                })
              }}
              className="flex items-center"
            >
              全选
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => clearBookmarkSelection()}
              className="flex items-center"
            >
              取消选择 (ESC)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBatchEdit(true)}
              className="flex items-center"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              批量编辑
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowValidator(true)}
              className="flex items-center"
            >
              <AlertCircle className="w-4 h-4 mr-1" />
              检查有效性
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm(`确定要删除选中的 ${selectedBookmarks.size} 个书签吗？`)) {
                  removeBookmarks([...selectedBookmarks])
                }
              }}
              className="flex items-center"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              删除所选 (Del)
            </Button>
          </div>
        </div>
      )}

      {/* 排序按钮组 */}
      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleSort('title')}
          className="flex items-center"
        >
          标题
          {sortField === 'title' && (
            <ArrowUpDown className={`ml-1 h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleSort('createdAt')}
          className="flex items-center"
        >
          创建时间
          {sortField === 'createdAt' && (
            <ArrowUpDown className={`ml-1 h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleSort('updatedAt')}
          className="flex items-center"
        >
          更新时间
          {sortField === 'updatedAt' && (
            <ArrowUpDown className={`ml-1 h-4 w-4 ${sortOrder === 'desc' ? 'transform rotate-180' : ''}`} />
          )}
        </Button>
      </div>

      {/* 书签列表 */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="bookmarks">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-y-auto"
            >
              {currentBookmarks.map((bookmark, index) => (
                <Draggable
                  key={bookmark.id}
                  draggableId={bookmark.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`
                        bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden
                        ${snapshot.isDragging ? 'shadow-lg' : ''}
                        ${selectedBookmarks.has(bookmark.id) ? 'ring-2 ring-blue-500' : ''}
                      `}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({
                          x: e.clientX,
                          y: e.clientY,
                          url: bookmark.url
                        });
                      }}
                    >
                      {/* 卡片头部 - 拖拽手柄和选择框 */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                          <div {...provided.dragHandleProps}>
                            <GripVertical className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="checkbox"
                            checked={selectedBookmarks.has(bookmark.id)}
                            onChange={() => toggleBookmarkSelection(bookmark.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleBookmarkFavorite(bookmark.id)}
                            className={`p-1 rounded-full hover:bg-gray-200 ${
                              bookmark.isFavorite ? 'text-yellow-500' : 'text-gray-400'
                            }`}
                          >
                            <Star className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setPreviewBookmark(bookmark)}
                            className="p-1 rounded-full hover:bg-gray-200 text-gray-400"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* 卡片内容 */}
                      <div className="p-4 space-y-3">
                        <div className="space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-medium text-gray-900 line-clamp-2 flex items-center gap-2">
                              {bookmark.favicon ? (
                                <img 
                                  src={bookmark.favicon} 
                                  alt="" 
                                  className="w-4 h-4 flex-shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <ExternalLink className="h-4 w-4 flex-shrink-0 text-gray-400" />
                              )}
                              {bookmark.title}
                            </h3>
                            <a
                              href={bookmark.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-gray-400 hover:text-gray-600"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                          {bookmark.description && (
                            <p className="text-sm text-gray-500 line-clamp-2">
                              {bookmark.description}
                            </p>
                          )}
                        </div>

                        {/* 分类 */}
                        <div className="text-sm text-gray-500">
                          {getCategoryName(bookmark.categoryId)}
                        </div>

                        {/* 标签 */}
                        <div className="flex flex-wrap gap-1">
                          {bookmark.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        {/* 操作按钮 */}
                        <div className="flex justify-end gap-2 pt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingBookmark(bookmark)}
                            className="flex items-center"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setBookmarkToDelete(bookmark)}
                            className="flex items-center text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {filteredAndSortedBookmarks.length === 0 && (
        <div className="p-4 text-center text-gray-500">
          {searchQuery ? '没有找到匹配的书签' : '暂无书签，点击上方按钮添加'}
        </div>
      )}

      {/* 分页控件 */}
      {filteredAndSortedBookmarks.length > 0 && totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 px-2">
          <div className="text-sm text-gray-500">
            共 {filteredAndSortedBookmarks.length} 个书签，第 {currentPage}/{totalPages} 页，当前页显示 {currentBookmarks.length} 个书签
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              首页
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {/* 页码按钮 */}
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // 显示当前页附近的页码
                let pageNum = currentPage;
                if (totalPages <= 5) {
                  // 如果总页数小于等于5，直接显示所有页码
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  // 如果当前页靠近开始，显示前5页
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // 如果当前页靠近结束，显示后5页
                  pageNum = totalPages - 4 + i;
                } else {
                  // 否则显示当前页及其前后各2页
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "primary" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              末页
            </Button>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
                setCurrentPage(1) // 重置到第一页
              }}
              className="h-8 rounded border-gray-300 text-sm"
            >
              <option value="16">16条/页</option>
              <option value="24">24条/页</option>
              <option value="32">32条/页</option>
              <option value="48">48条/页</option>
            </select>
          </div>
        </div>
      )}

      {/* 书签预览对话框 */}
      {previewBookmark && (
        <BookmarkPreview
          bookmark={previewBookmark}
          onClose={() => setPreviewBookmark(null)}
        />
      )}

      {/* 其他对话框组件 */}
      {showForm && (
        <BookmarkForm onClose={() => setShowForm(false)} />
      )}
      {editingBookmark && (
        <BookmarkForm
          bookmark={editingBookmark}
          onClose={() => setEditingBookmark(null)}
        />
      )}
      {bookmarkToDelete && (
        <ConfirmDialog
          title="删除书签"
          message={`确定要删除书签"${bookmarkToDelete.title}"吗？`}
          onConfirm={() => {
            removeBookmark(bookmarkToDelete.id)
            setBookmarkToDelete(null)
          }}
          onCancel={() => setBookmarkToDelete(null)}
        />
      )}
      {showBatchEdit && (
        <BatchEditDialog
          onClose={() => setShowBatchEdit(false)}
        />
      )}
      {showHotkeys && (
        <HotkeysHelp onClose={() => setShowHotkeys(false)} />
      )}
      {showValidator && (
        <BookmarkValidator onClose={() => setShowValidator(false)} />
      )}
      {showAnalytics && (
        <BookmarkAnalytics onClose={() => setShowAnalytics(false)} />
      )}

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          url={contextMenu.url}
          onClose={() => setContextMenu(null)}
          theme={String(theme) === 'dark' ? 'dark' : 'light'}
        />
      )}
    </div>
  )
} 